package org.moxultimate.topwithmox.components.loading

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.moxultimate.topwithmox.components.texts.MoxGradientText
import org.moxultimate.topwithmox.theme.SemiWhiteTransparentBG

@Composable
fun LoadingIndicator() {
    Column(
        modifier = Modifier
            .width(100.vw)
            .height(100.vh)
            .backgroundColor(SemiWhiteTransparentBG),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Image(
            src = "/ic_topwithmox.webp",
            modifier = PulsingImageStyle
                .toModifier()
                .width(100.px)
                .height(150.px)
        )

        MoxGradientText(
            text = "Loading...",
            fontSize = 20,
            modifier = FadingTextStyle
                .toModifier()
                .padding(top = 8.px)
        )
    }
}

/**
 * OverlayLoadingIndicator
 * Fixed-position overlay with a smooth fade-in / fade-out transition.
 * Always kept in the DOM — opacity drives visibility so the CSS transition fires on both
 * enter and exit. pointer-events are disabled while invisible so it never blocks clicks.
 *
 * @param visible True while the overlay should be shown (opacity 1), false to fade out.
 */
@Composable
fun OverlayLoadingIndicator(visible: Boolean) {
    Column(
        modifier = Modifier
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("z-index", "50")
                property("transition", "opacity 0.3s ease")
                property("pointer-events", if (visible) "auto" else "none")
            }
            .width(100.vw)
            .height(100.vh)
            .opacity(if (visible) 1.0 else 0.0)
            .backgroundColor(SemiWhiteTransparentBG),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Image(
            src = "/ic_topwithmox.webp",
            modifier = PulsingImageStyle
                .toModifier()
                .width(100.px)
                .height(150.px)
        )
        MoxGradientText(
            text = "Loading...",
            fontSize = 20,
            modifier = FadingTextStyle
                .toModifier()
                .padding(top = 8.px)
        )
    }
}

/** Compact inline spinner shown at the bottom of a list while the next page loads. */
@Composable
fun LoadingMoreIndicator() {
    Column(
        modifier = Modifier.padding(topBottom = 8.px),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Image(
            src = "/ic_topwithmox.webp",
            modifier = PulsingImageStyle
                .toModifier()
                .width(40.px)
                .height(60.px)
        )
        MoxGradientText(
            text = "Loading more...",
            fontSize = 14,
            modifier = FadingTextStyle
                .toModifier()
                .padding(top = 4.px)
        )
    }
}

val PulseKeyframes = Keyframes {
    from {
        Modifier
            .width(100.px)
            .height(150.px)
    }
    to {
        Modifier
            .width(150.px)
            .height(200.px)
    }
}

val PulsingImageStyle = CssStyle {
    base {
        Modifier.animation(
            PulseKeyframes.toAnimation(
                duration = 1.2.s,
                timingFunction = AnimationTimingFunction.EaseInOut,
                direction = AnimationDirection.Alternate,
                iterationCount = AnimationIterationCount.Infinite
            )
        )
    }
}


// --- New Text Fade Animation ---
val TextFadeKeyframes = Keyframes {
    from {
        Modifier.opacity(0.0) // Hidden when image is small
    }
    to {
        Modifier.opacity(1.0) // Fully visible when image reaches peak size
    }
}

val FadingTextStyle = CssStyle {
    base {
        Modifier
            .color(Color.black) // Style your text color as needed
            .fontWeight(FontWeight.Bold)
            .fontSize(1.2.cssRem)
            .animation(
                TextFadeKeyframes.toAnimation(
                    duration = 1.8.s, // Must match the image duration exactly
                    timingFunction = AnimationTimingFunction.EaseInOut,
                    direction = AnimationDirection.Alternate,
                    iterationCount = AnimationIterationCount.Infinite
                )
            )
    }
}